import React, { useEffect, useState } from "react";
import { Search, ArrowRight } from "lucide-react";

/**
 * TickerSearchBar — input that lets the user type any U.S.-listed
 * ticker symbol and pull its research snapshot.
 *
 * Doesn't do any data fetching itself — calls onSubmit(ticker) with
 * the normalized symbol. The parent decides what to do (open the
 * StockResearchModal, log the search, etc.).
 *
 * Format validation is purely client-side and lenient: 1-5 uppercase
 * letters, optionally followed by .X (for class shares like BRK.B).
 * Everything else gets a friendly inline hint instead of blocking.
 *
 * Recent-searches MRU list persists in localStorage (top 5) so users
 * can re-open recently-viewed tickers in one click. Wiped if the
 * user logs out (via the parent that decides to render us).
 *
 * Props:
 *   onSubmit       — Required. Called with the validated, uppercased
 *                    ticker string.
 *   placeholder    — Optional override for the input placeholder.
 *   recentLimit    — Optional. Defaults to 5.
 */

const VALID_TICKER = /^[A-Z]{1,5}(\.[A-Z])?$/;
const STORAGE_KEY = "arcResearch:recentTickers:v1";

function loadRecents() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === "string") : [];
  } catch (_) {
    return [];
  }
}

function saveRecents(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (_) {
    /* localStorage full / disabled — fail silently */
  }
}

const TickerSearchBar = ({
  onSubmit,
  placeholder = "Type any U.S. ticker (e.g. AAPL, NVDA, BRK.B)",
  recentLimit = 5,
}) => {
  const [value, setValue] = useState("");
  const [hint, setHint] = useState(null);
  const [recents, setRecents] = useState(() => loadRecents());

  // Keep recents synced if other tabs update them.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setRecents(loadRecents());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleSubmit = (raw) => {
    const ticker = String(raw || value)
      .toUpperCase()
      .trim();

    if (!ticker) {
      setHint("Type a ticker symbol to look up.");
      return;
    }
    if (!VALID_TICKER.test(ticker)) {
      setHint(
        `"${ticker}" doesn't look like a stock ticker. Use 1–5 letters (e.g. AAPL), or class shares like BRK.B.`
      );
      return;
    }

    // Bump to top of recents (de-duped)
    const nextRecents = [
      ticker,
      ...recents.filter((t) => t !== ticker),
    ].slice(0, recentLimit);
    setRecents(nextRecents);
    saveRecents(nextRecents);

    setHint(null);
    onSubmit?.(ticker);
  };

  const handleClearRecent = (ticker, e) => {
    // Stop the chip's outer onClick from firing the search
    e.stopPropagation();
    const next = recents.filter((t) => t !== ticker);
    setRecents(next);
    saveRecents(next);
  };

  return (
    <div className="surface-xl p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-2">
        <Search size={14} className="text-arc-gold" />
        <p className="eyebrow">Look up any stock</p>
      </div>
      <p className="text-xs text-arc-muted mb-3 leading-relaxed">
        Pull a grounded equity analysis from any U.S.-listed ticker.
        Sourced from SEC filings, generated by ARCrypto AI. First lookup
        takes ~60 seconds, repeat lookups load instantly from cache.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="flex flex-col sm:flex-row gap-2"
      >
        <div className="relative flex-1">
          <input
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value.toUpperCase());
              if (hint) setHint(null);
            }}
            placeholder={placeholder}
            spellCheck={false}
            autoCapitalize="characters"
            autoCorrect="off"
            className="w-full bg-arc-bg border border-arc-line rounded-lg pl-3 pr-3 py-2 text-sm text-arc-ink font-mono uppercase tracking-wide placeholder:normal-case placeholder:font-sans placeholder:text-arc-dim focus:outline-none focus:border-arc-gold transition-colors"
          />
        </div>
        <button
          type="submit"
          className="btn-tactile btn-gold inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium"
        >
          Research
          <ArrowRight size={14} />
        </button>
      </form>

      {hint && (
        <p className="mt-2 text-xs text-amber-300 leading-relaxed">{hint}</p>
      )}

      {recents.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim mb-2">
            Recent
          </p>
          <div className="flex flex-wrap gap-1.5">
            {recents.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => handleSubmit(t)}
                className="btn-tactile group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-arc-line bg-transparent text-xs font-mono text-arc-muted hover:text-arc-ink hover:border-arc-gold/40 transition-all"
              >
                {t}
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Remove ${t} from recents`}
                  onClick={(e) => handleClearRecent(t, e)}
                  className="opacity-0 group-hover:opacity-100 -mr-0.5 px-1 text-arc-dim hover:text-red-300 transition-opacity"
                >
                  ×
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TickerSearchBar;
